import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { MetaData } from '../types/widget';
import { InitialShowOrgFilterState } from '../constants/filter/filter';

interface WidgetsModalContextProps {
  selfData: any;
  setSelfData: Dispatch<SetStateAction<any>>;

  layoutData: any;
  setLayoutData: Dispatch<SetStateAction<any>>;

  layoutLoading: any;
  setLayoutLoading: Dispatch<SetStateAction<boolean>>;
  widgetIds: any;
  setWidgetIds: Dispatch<SetStateAction<any>>;
  widgetLoadingStates: { [key: string]: boolean };
  setWidgetLoadingStates: Dispatch<SetStateAction<{ [key: string]: boolean }>>;
  widgetApiCalled: { [key: string]: boolean };
  setWidgetApiCalled: Dispatch<SetStateAction<{ [key: string]: boolean }>>;
  widgetData: MetaData;
  setWidgetData: Dispatch<SetStateAction<{ [key: string]: any }>>;
  feedbackPopupOpen: boolean;
  setFeedbackPopupOpen: Dispatch<SetStateAction<boolean>>;
  feedbackSuccessOpen: boolean;
  setFeedbackSuccessOpen: Dispatch<SetStateAction<boolean>>;

  feedbackWidget: boolean;
  setFeedbackWidget: Dispatch<SetStateAction<boolean>>;

  showFilterModal: boolean;
  setShowFilterModal: Dispatch<SetStateAction<boolean>>;

  showMobileFilterModal: boolean;
  setMobileShowFilterModal: Dispatch<SetStateAction<boolean>>;

  showOrgFilter: any;
  setShowOrgFilter: Dispatch<SetStateAction<any>>;

  showDatePicker: boolean;
  setShowDatePicker: Dispatch<SetStateAction<boolean>>;

  showBottomSheet: boolean;
  setShowBottomSheet: Dispatch<SetStateAction<boolean>>;

  selectedAccorionFilter: number | null;
  setSelectedAccorionFilter: Dispatch<SetStateAction<number | null>>;

  showDownloadReport: boolean;
  setShowDownloadReport: Dispatch<SetStateAction<boolean>>;

  // Tracks whether the layout API has resolved and date filters are ready.
  // Widget APIs must not fire until this is true, otherwise they use stale default dates.
  isLayoutReady: boolean;
  setIsLayoutReady: Dispatch<SetStateAction<boolean>>;
}

const WidgetsModalContext = createContext<WidgetsModalContextProps | undefined>(
  undefined,
);

export const useWidgetsModalContext = (): WidgetsModalContextProps => {
  const context = useContext(WidgetsModalContext);
  if (!context) {
    throw new Error(
      'useWidgetsModalContext must be used within a ModalProvider',
    );
  }
  return context;
};

interface WidgetsModalProviderProps {
  children: ReactNode;
}

export const WidgetsModalProvider: FC<WidgetsModalProviderProps> = ({
  children,
}) => {
  const [selfData, setSelfData] = useState([]);

  const [layoutData, setLayoutData] = useState({});
  const [layoutLoading, setLayoutLoading] = useState<boolean>(true);
  const [widgetIds, setWidgetIds] = useState<string[]>([]);

  const [widgetLoadingStates, setWidgetLoadingStates] = useState<{
    [key: string]: boolean;
  }>({});

  const [widgetApiCalled, setWidgetApiCalled] = useState<{
    [key: string]: boolean;
  }>({});

  const [widgetData, setWidgetData] = useState<{
    [key: string]: any;
  }>({});

  const [feedbackPopupOpen, setFeedbackPopupOpen] = useState<boolean>(false);
  const [feedbackSuccessOpen, setFeedbackSuccessOpen] =
    useState<boolean>(false);
  const [feedbackWidget, setFeedbackWidget] = useState<boolean>(true);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [showMobileFilterModal, setMobileShowFilterModal] =
    useState<boolean>(false);
  const [showOrgFilter, setShowOrgFilter] = useState<any>(
    InitialShowOrgFilterState,
  );
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false);
  const [selectedAccorionFilter, setSelectedAccorionFilter] = useState<
    any | null
  >(null);
  const [showDownloadReport, setShowDownloadReport] = useState<boolean>(false);

  const [isLayoutReady, setIsLayoutReady] = useState<boolean>(false);

  const value: WidgetsModalContextProps = {
    selfData,
    setSelfData,
    layoutData,
    setLayoutData,
    layoutLoading,
    setLayoutLoading,
    widgetIds,
    setWidgetIds,
    widgetLoadingStates,
    setWidgetLoadingStates,
    widgetApiCalled,
    setWidgetApiCalled,
    widgetData,
    setWidgetData,
    feedbackPopupOpen,
    setFeedbackPopupOpen,
    feedbackSuccessOpen,
    setFeedbackSuccessOpen,
    feedbackWidget,
    setFeedbackWidget,
    showFilterModal,
    setShowFilterModal,
    showOrgFilter,
    setShowOrgFilter,
    showDatePicker,
    setShowDatePicker,
    showMobileFilterModal,
    setMobileShowFilterModal,
    showBottomSheet,
    setShowBottomSheet,
    selectedAccorionFilter,
    setSelectedAccorionFilter,
    showDownloadReport,
    setShowDownloadReport,
    isLayoutReady,
    setIsLayoutReady,
  };

  return (
    <WidgetsModalContext.Provider value={value}>
      {children}
    </WidgetsModalContext.Provider>
  );
};
